import { useState } from 'react'

import { useFlags } from '@joor/launchdarkly-react-client-sdk'
import { find } from 'lodash'
import difference from 'lodash/difference'
import { List } from 'semantic-ui-react'

import { OrderStatus } from '__generated__/atlas-types'

import { Order } from 'types'
import { makeRequest } from 'utils/requests'
import { fromGlobalId, toGlobalId } from 'utils/transformations/graphql'

import Checkbox from 'components/Core/Checkbox/Checkbox'
import Dropdown from 'components/Core/Dropdown/Dropdown'
import Modal from 'components/Core/Modal'

import messages from './ChangeStatusModal.messages'
import { useBulkUpdateOrderStatus } from './ChangeStatusModal.mutations'
import {
  LegacyOption,
  getDropdownOptions,
  getPhpRequestData,
  restructureResponse,
  shouldCheckMinimums,
} from './ChangeStatusModal.utils'

export type SelectedOrderType = Pick<Order, 'id' | 'webOrderLookupId'>

type ChangeStatusModalType = {
  handleResetOrder: () => void
  isRetailer: boolean
  onClose: () => void
  refetchStaticCounts: () => void
  renderActionsToasterMessage: (
    updatedValues: number,
    notUpdatedValues: number,
  ) => void
  selectedOrders: SelectedOrderType[]
  setPageLoaderActive: (loading: boolean) => void
  trackChangeStatusModalSubmitted?: (newStatus: OrderStatus) => void
  trackChangeStatusModalCancelled?: () => void
  onSuccess?: (updated: Record<string, OrderStatus>) => void
}

export const ChangeStatusModal = ({
  handleResetOrder,
  isRetailer,
  onClose,
  refetchStaticCounts,
  renderActionsToasterMessage,
  selectedOrders,
  setPageLoaderActive,
  trackChangeStatusModalSubmitted,
  trackChangeStatusModalCancelled,
  onSuccess,
}: ChangeStatusModalType) => {
  const [status, setStatus] = useState(0)
  const [selectedStatus, setSelectedStatus] = useState(OrderStatus.NOTES)
  const [emailBrand, setEmailBrand] = useState(false)
  const [emailRetailer, setEmailRetailer] = useState(false)

  const { onBulkUpdateStatus } = useBulkUpdateOrderStatus()
  const { atlasChangeStatus } = useFlags()

  const handleSuccessStatusChange = (updatedIds: string[]) => {
    const result: Record<string, OrderStatus> = {}
    for (const id of updatedIds) {
      result[id] = selectedStatus
    }
    onSuccess?.(result)
  }

  const changeStatus = (validOrderIds: string[]): unknown => {
    if (!validOrderIds) {
      return Promise.resolve()
    }
    setPageLoaderActive(true)
    const url = '/json/orders/sync'
    const data = getPhpRequestData({
      validOrderIds,
      status,
      emailBrand,
      emailRetailer,
    })

    // TODO(ch15461) rebuild endpoint
    return makeRequest({
      url,
      data,
      method: 'POST',
      headers: {
        'content-type': 'application/x-www-form-urlencoded',
      },
    })
      .then((response) => {
        const batchQueryResponse = restructureResponse(response, validOrderIds)
        return Promise.all([
          handleResetOrder(),
          refetchStaticCounts(),
          batchQueryResponse,
        ])
      })
      .then(([, , batchQueryResponse]) => {
        setPageLoaderActive(false)

        // updatedOrders is an array of string ids not global encoded
        if (batchQueryResponse.updatedOrders.length && onSuccess) {
          const encodedOrderIds = batchQueryResponse.updatedOrders.map(
            (id: string) => toGlobalId('Order', id),
          )
          handleSuccessStatusChange(encodedOrderIds)
        }
        renderActionsToasterMessage(
          batchQueryResponse.updatedOrders.length,
          batchQueryResponse.notUpdatedOrders.length,
        )
      })
      .catch((error) => {
        console.error(error)
        setPageLoaderActive(false)
      })
  }

  const checkMinimumsIfNeeded = () => {
    const selectedPublicOrderIds = selectedOrders.map(
      (order: SelectedOrderType) => fromGlobalId(order.id).id,
    )

    if (shouldCheckMinimums(status)) {
      const selectedWebOrderIds = selectedOrders.map(
        (order) => order.webOrderLookupId,
      )
      const url = `/json/orders/checkMinimums/${selectedWebOrderIds.join()}`
      setPageLoaderActive(true)
      // TODO(ch15461) rebuild endpoint
      return makeRequest({ url })
        .then(({ data }) => {
          const invalidOrders = data.orders || {}
          const invalidOrderIds = Object.keys(invalidOrders).map((order) =>
            invalidOrders[order].order_id.toString(),
          )

          if (invalidOrderIds && invalidOrderIds.length) {
            // TODO(santiago): remove this once we finish CH6448
            // eslint-disable-next-line no-console
            console.log(
              'The following orders did not meet the Order or Style minimums',
              invalidOrderIds,
            )
          }
          setPageLoaderActive(false)
          return difference(selectedPublicOrderIds, invalidOrderIds)
        })
        .catch((error) => {
          console.error(error)
          setPageLoaderActive(false)
          return Promise.resolve([])
        })
    }
    return Promise.resolve(selectedPublicOrderIds)
  }

  const handleOnSubmit = async () => {
    trackChangeStatusModalSubmitted?.(selectedStatus)
    if (atlasChangeStatus) {
      setPageLoaderActive(true)
      const updatedIds = await onBulkUpdateStatus(
        selectedOrders.map((order) => order.id),
        selectedStatus,
      )
      if (updatedIds && updatedIds.length && onSuccess) {
        handleSuccessStatusChange(updatedIds)
      }
      Promise.all([handleResetOrder(), refetchStaticCounts()]).then(() => {
        setPageLoaderActive(false)
      })
    } else {
      checkMinimumsIfNeeded().then(changeStatus)
    }
  }

  const handleOnChange = (
    _event: unknown,
    { value, options }: { value: number; options: LegacyOption[] },
  ) => {
    setSelectedStatus(
      find(options, (option: LegacyOption) => option.value === value)
        ?.code as OrderStatus,
    )
    setStatus(value)
  }

  const toggleEmailOption = (_event: unknown, { value }: { value: string }) => {
    value === 'emailBrand'
      ? setEmailBrand(!emailBrand)
      : setEmailRetailer(!emailRetailer)
  }

  const handleOnClose = () => {
    trackChangeStatusModalCancelled?.()
    onClose()
  }

  return (
    <Modal
      title={messages.changeStatus}
      primaryActionLabel={messages.update}
      primaryActionOnClick={handleOnSubmit}
      primaryDisabled={!status}
      open
      onClose={handleOnClose}
    >
      <label htmlFor="change-status">{messages.selectOption}</label>
      <Dropdown
        placeholder={messages.selectOne}
        options={getDropdownOptions(isRetailer)}
        onChange={handleOnChange}
        id="change-status"
      />
      <List>
        <List.Item>
          <Checkbox
            label={messages.emailBrand}
            value="emailBrand"
            checked={emailBrand}
            onClick={toggleEmailOption}
          />
        </List.Item>
        <List.Item>
          <Checkbox
            label={messages.emailRetailer}
            value="emailRetailer"
            checked={emailRetailer}
            onClick={toggleEmailOption}
          />
        </List.Item>
      </List>
    </Modal>
  )
}

export default ChangeStatusModal
